// Gift.jsx
import React, { createRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { withTranslation } from 'react-i18next';

import DecryptionWrapper from '../container/DecryptionWrapper.js';
import GiftPreviewContext from '../context/GiftPreviewContext.jsx';
import PagGiftPreview from '../component/PagGiftPreview.jsx';

import StatefulImage from '../component/StatefulImage.jsx';
import LazyImage, { Image } from '../component/LazyImage.jsx';
import media from '../style/media.js';
import { color, textColor } from '../style/variables.js';

import env from '../resource/env.js';
import getPublicSrcSet from '../resource/getPublicSrcSet.js';
import { MediaAssetFormat } from '../resource/getMediaAsset.js';
import trackMixpanel, { getViewId } from '../resource/mixpanel.js';
import {
  ButtonId,
  EventTypes,
  ModalId,
} from '../resource/trackingConstants.js';
import getAPNG from '../resource/getAPNG.js';
import getIsHoverCapableDevice from '../resource/getIsHoverCapableDevice.js';
import getPag, { isPagCached } from '../resource/getPag.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import { NULL_FUNCTION } from '../resource/defaults.js';
import { livestream as livestreamDebug } from '../resource/debug.js';
import { startSentryLogCapture } from '../resource/sentry.js';
import { LOG_CAPTURE_LIVESTREAM_SEND_GIFT } from '../resource/sentryLogCaptureNames.js';

import LoadingImage from '../../img/placeholder-gift.svg';
import LoadingImageDark from '../../img/img-loading-dark-20.svg';
import DownloadIcon from '../../img/icon-download-green.svg';
import WarningIcon from '../../img/icon-warning-gray.svg';
import GiftPlaceholder from '../../img/img_gift_placeholder.svg';
import DiamondImage from '../../img/point_2.svg';
import DiamondGreyImage from '../../img/icon-diamond-grey.svg';

import { Entrypoint } from '../resource/shopConstants.js';

const log = livestreamDebug.extend('log:Gift');
const errorLog = livestreamDebug.extend('error:Gift');

const giftAllowTypes = ['panel', 'livestream'];
const giftButtonIdList = {
  panel: ButtonId.All.ButtonGiftChatroom,
  livestream: ButtonId.All.ButtonGiftLiveStream,
};

export class Gift extends React.PureComponent {
  static contextType = GiftPreviewContext;

  state = {
    isHovered: false,
    isLoading: false,
    isLoadSucceed: null,
    hasParsed: null,
    // True only after PagGiftPreview reports `onPlay` — gates hiding the
    // static PNG thumbnail so the user never sees a blank canvas mid-load.
    isPagGiftPreviewPlaying: false,
  };
  isPagDownloading = false;
  isPendingHidePreview = false;
  canvasRef = createRef();
  apngPlayer = null;
  parsedApng = null;
  componentDidMount() {
    this.checkPagCache();
    this.checkAPNG();
  }
  componentDidUpdate(prevProps) {
    const { isActive, giftSendingStatus } = this.props;
    if (isActive && isActive !== prevProps.isActive) {
      this.checkAPNG();
    }
    // Re-checks every update — hover/select flow through context, not props.
    // `numPlays === 0` already means "APNG is currently looping"
    if (this.parsedApng) {
      const shouldAPNGPlay = this.getIsPreviewTargetGift();
      const isAPNGPlaying = this.parsedApng.numPlays === 0;
      if (shouldAPNGPlay !== isAPNGPlaying) {
        this.parsedApng.numPlays = shouldAPNGPlay ? 0 : 1; // 0 (loop playing) and 1 (play once & stop)
        if (shouldAPNGPlay) {
          this.apngPlayer?.play();
          this.trackAnimationPlayed({ animationType: 'apng' });
        } else {
          this.apngPlayer?.stop();
        }
      }
    }
    if (
      this.isPendingHidePreview &&
      prevProps.giftSendingStatus === 'sending' &&
      giftSendingStatus !== 'sending'
    ) {
      if (giftSendingStatus === 'success' && this.getIsSelected()) {
        this.context?.hidePreview();
      }
      this.isPendingHidePreview = false;
    }
  }
  componentWillUnmount() {
    if (this.apngPlayer) {
      this.apngPlayer.stop();
      this.apngPlayer = null;
    }
    this.parsedApng = null;
  }
  checkPagCache() {
    const { pagImage, isPagGift } = this.props;
    if (!isPagGift || !pagImage) {
      return;
    }

    // Check if PAG is already in cache (no network request)
    const isCached = isPagCached(pagImage);
    this.setState({ hasParsed: isCached ? true : false });
  }
  handleOnMouseEnter = () => {
    if (!getIsHoverCapableDevice()) return;
    this.setState({ isHovered: true });
    if (!this.context) return;
    const { id, image, pagImage, isPagGift, isAnimationGift } = this.props;
    this.context.hoverGift({
      id,
      image,
      pagImage,
      isPagGift,
      isAnimationGift,
    });
  };
  handleOnMouseLeave = () => {
    if (!getIsHoverCapableDevice()) return;
    this.setState({ isHovered: false });
    if (!this.context) return;
    this.context.unhoverGift();
  };

  // Hover state takes precedence. If there is no hovered item, fall back to the selected item
  // But only when the preview panel is currently visible.
  getIsPreviewTargetGift = () => {
    const { id } = this.props;
    const hoveredGift = this.context?.hoveredGift;
    if (hoveredGift) return hoveredGift.id === id;
    const isPreviewVisible = this.context?.isPreviewVisible;
    const selectedGift = this.context?.selectedGift;
    return !!isPreviewVisible && selectedGift?.id === id;
  };

  handleSendButtonClick = event => {
    event.stopPropagation();
    if (this.getIsSendButtonLoading()) return;
    return this.handleSendGift();
  };

  trackAnimationPlayed = ({ animationType }) => {
    const { id, streamId } = this.props;
    trackMixpanel({
      type: EventTypes.LIVESTREAM_GIFT_ANIMATION_PLAYED,
      payload: {
        viewId: getViewId({ pathname: window.location.pathname }),
        modalId: ModalId.LiveStream.ModalPreviewGiftAnimation,
        giftId: id,
        streamId,
        animationType,
      },
    });
  };

  // Reuse `isLoadSucceed` so the same corner WarningIcon lights up on preview
  // failure. Success sets `hasParsed: true` to clear the PAG download cue.
  handlePagGiftPreviewSuccess = () => {
    this.setState({
      isLoadSucceed: true,
      hasParsed: true,
      isPagGiftPreviewPlaying: true,
    });
    this.trackAnimationPlayed({ animationType: 'pag' });
  };
  handlePagGiftPreviewFailure = () => {
    this.setState({ isLoadSucceed: false });
  };
  // Reset on child unmount so the next hover starts with the PNG visible
  // until the new PAG instance reports `onPlay`.
  handlePagGiftPreviewUnmount = () => {
    this.setState({ isPagGiftPreviewPlaying: false });
  };

  getIsSendButtonLoading = () => {
    const { id, isSendingGift } = this.props;
    return !!isSendingGift || this.context?.loadingPreviewGiftId === id;
  };
  handleOnClick = async () => {
    if (this.props.isSwiping) return;
    if (this.props.isSendingGift || this.state.isLoading) return;
    // Fallback: when not inside GiftPreviewProvider (chatroom panel, confirm
    // modal preview, etc.) preserve the original click-to-send behavior.
    if (!this.context) return this.handleSendGift();

    const { id, image, pagImage, isPagGift, isAnimationGift } = this.props;
    this.context.selectGift({
      id,
      image,
      pagImage,
      isPagGift,
      isAnimationGift,
    });
  };

  handleSendGift = async () => {
    const {
      id,
      isSwiping,
      notifySendGift,
      sendGift,
      isAnimationGift,
      isPagGift,
      hasEnoughBalance,
      addvalue,
      isLoggedIn,
      openAnimationFailedModal,
    } = this.props;

    if (isSwiping) {
      return;
    }

    if (!isLoggedIn || !hasEnoughBalance) {
      addvalue({
        entrypoint: Entrypoint.GIFT,
        via: 'gift',
      });
      return;
    }

    startSentryLogCapture({ captureName: LOG_CAPTURE_LIVESTREAM_SEND_GIFT });
    log(`gift [${id}] clicked by user`, { giftId: id, isPagGift });

    const isPreviewAnimationFailed =
      this.context?.previewErrorGiftId === id ||
      this.state.isLoadSucceed === false;
    if ((isPagGift || isAnimationGift) && isPreviewAnimationFailed) {
      log(`gift [${id}] preview not ready on send, prompting failure modal`, {
        giftId: id,
      });
      openAnimationFailedModal({ giftId: id, isAnimationGift, isPagGift });
      return;
    }

    // pag gift click to download
    if (isPagGift && !this.state.hasParsed) {
      if (this.isPagDownloading) {
        return;
      }

      log(`gift [${id}] PAG download`, { giftId: id });
      this.isPagDownloading = true;
      const isPagSuccess = await this.checkPagGift();
      this.isPagDownloading = false;

      if (!isPagSuccess) return;
    }

    if (isAnimationGift || isPagGift) {
      notifySendGift({ giftId: id });
    } else {
      sendGift({ giftId: id });
    }

    // Defer hidePreview until the send actually succeeds — see
    // `componentDidUpdate` for the `'sending' → 'success'` transition watch.
    // Failure / still-in-flight keeps the preview open so the user can retry.
    this.isPendingHidePreview = true;
  };

  async checkPagGift() {
    const { pagImage, id, openAnimationFailedModal } = this.props;

    if (!pagImage) {
      errorLog(`gift [${id}] PAG download failed: no pagImage`, { giftId: id });
      return false;
    }

    this.setState({ isLoading: true });
    const { isPAGSupported, pag } = await getPag({
      imageSource: pagImage,
    });
    if (isPAGSupported && pag) {
      log(`gift [${id}] PAG download success`, { giftId: id });
      this.setState({
        isLoading: false,
        isLoadSucceed: true,
        hasParsed: true,
      });
      return true;
    } else {
      errorLog(`gift [${id}] PAG download failed`, {
        giftId: id,
        isPAGSupported,
        hasPag: !!pag,
      });
      this.setState({
        isLoading: false,
        isLoadSucceed: false,
        hasParsed: false,
      });
      openAnimationFailedModal({
        giftId: id,
        isAnimationGift: false,
        isPagGift: true,
      });
      return false;
    }
  }

  async checkAPNG() {
    const { image, isAnimationGift, isActive, id, openAnimationFailedModal } =
      this.props;

    if (!isAnimationGift || !isActive) return;

    if (this.apngPlayer) {
      this.apngPlayer.stop();
      this.apngPlayer = null;
    }

    this.setState({ isLoading: true });

    const context = this.canvasRef.current.getContext('2d');
    const loadingImage = new Image();
    loadingImage.src = LoadingImage;
    loadingImage.onload = () => {
      context.drawImage(loadingImage, 0, 0);
    };

    const { isAPNG, apng } = await getAPNG({ imageSource: image });
    if (!isAPNG || !this.canvasRef.current) {
      log(`checkAPNG failed: ${id}`, {
        giftId: id,
        isAPNG,
        hasCanvas: !!this.canvasRef.current,
      });
      this.setState({
        isLoading: false,
        isLoadSucceed: false,
        hasParsed: false,
      });
      openAnimationFailedModal({
        giftId: id,
        isAnimationGift: true,
        isPagGift: false,
      });
      return;
    }

    this.setState({ hasParsed: true });

    this.canvasRef.current.width = apng.width;
    this.canvasRef.current.height = apng.height;

    if (this.getIsPreviewTargetGift()) apng.numPlays = 0;
    this.parsedApng = apng;
    const player = await apng.getPlayer(
      this.canvasRef.current.getContext('2d')
    );
    this.apngPlayer = player;
    player.play();

    this.setState({ isLoading: false, isLoadSucceed: true });
  }

  renderPngImage() {
    const { image } = this.props;
    const isPublicImage = image.includes(env.PUBLIC_URL_PREFIX);

    if (isPublicImage) {
      const [avifSrcSet, webpSrcSet, pngSrcSet] = [
        MediaAssetFormat.AVIF,
        MediaAssetFormat.WEBP,
        MediaAssetFormat.PNG,
      ].map(format => getPublicSrcSet({ href: image, size: 128, format }));

      return (
        <LazyImage data-key="target" threshold={0.25}>
          <DecryptionWrapper resourceUrl={pngSrcSet.src}>
            <source srcSet={avifSrcSet.srcSet} type="image/avif" />
            <source srcSet={webpSrcSet.srcSet} type="image/webp" />
            <Image {...pngSrcSet} />
          </DecryptionWrapper>
        </LazyImage>
      );
    } else {
      return (
        <LazyImage data-key="target" threshold={0.25}>
          <Image src={image} />
        </LazyImage>
      );
    }
  }

  renderImage() {
    const { image, isAnimationGift, isPagGift, id, pagImage } = this.props;
    const {
      isHovered,
      isLoading,
      isLoadSucceed,
      hasParsed,
      isPagGiftPreviewPlaying,
    } = this.state;

    // Desktop only — touch devices show the preview in `GiftPreviewPanel`
    // instead. Two PAG players competing for the same asset starves both.
    const shouldShowPagGiftPreview =
      getIsHoverCapableDevice() &&
      isPagGift &&
      !!pagImage &&
      this.getIsPreviewTargetGift();
    // Hide the static thumbnail only when PAG is *actively* drawing, not just
    // when the overlay is mounted — so the user sees the PNG during loading.
    const isPngImageHidden =
      shouldShowPagGiftPreview && isPagGiftPreviewPlaying;

    const hasPreviewError =
      isLoadSucceed === false || this.context?.previewErrorGiftId === id;

    const isPagDownloaded = isPagCached(pagImage);

    if (isPagGift) {
      return (
        <PagGiftContainer>
          <PngImageLayer
            $isLoading={isLoading}
            $isHovered={isHovered}
            $isHidden={isPngImageHidden}
          >
            <StatefulImage>
              <StatefulImageWrapper data-key="loading">
                <Loader src={LoadingImage} />
              </StatefulImageWrapper>
              <StatefulImageWrapper data-key="error">
                <img src={GiftPlaceholder} />
              </StatefulImageWrapper>
              {this.renderPngImage()}
            </StatefulImage>
          </PngImageLayer>
          {shouldShowPagGiftPreview && (
            <PagPreviewLayer>
              <PagGiftPreview
                id={id}
                pagImage={pagImage}
                onSuccess={this.handlePagGiftPreviewSuccess}
                onFailure={this.handlePagGiftPreviewFailure}
                onUnmount={this.handlePagGiftPreviewUnmount}
              />
            </PagPreviewLayer>
          )}
          {isLoading && (
            <CenterIconOverlay>
              <SpinningLoading src={LoadingImageDark} />
            </CenterIconOverlay>
          )}
          {!isLoading &&
            hasParsed === false &&
            isLoadSucceed === null &&
            !hasPreviewError &&
            !isPagDownloaded && (
              <TopRightIconOverlay>
                <DownloadIconImage src={DownloadIcon} />
              </TopRightIconOverlay>
            )}
          {!isLoading && hasPreviewError && (
            <TopRightIconOverlay>
              <WarningIconImage src={WarningIcon} />
            </TopRightIconOverlay>
          )}
        </PagGiftContainer>
      );
    } else if (isAnimationGift) {
      return (
        <ApngGiftContainer>
          <CanvasLayer $isLoading={isLoading}>
            <AnimationGift width={72} height={72} ref={this.canvasRef} />
          </CanvasLayer>
          {isLoading && (
            <CenterIconOverlay>
              <SpinningLoading src={LoadingImageDark} />
            </CenterIconOverlay>
          )}
          {!isLoading && hasPreviewError && (
            <TopRightIconOverlay>
              <WarningIconImage src={WarningIcon} />
            </TopRightIconOverlay>
          )}
        </ApngGiftContainer>
      );
    }

    const isPublicImage = image.includes(env.PUBLIC_URL_PREFIX);
    let targetImageElement = null;
    if (isPublicImage) {
      const [avifSrcSet, webpSrcSet, pngSrcSet] = [
        MediaAssetFormat.AVIF,
        MediaAssetFormat.WEBP,
        MediaAssetFormat.PNG, // should use PNG for transparent background
      ].map(format => getPublicSrcSet({ href: image, size: 128, format }));
      targetImageElement = (
        <LazyImage data-key="target" threshold={0.25}>
          <DecryptionWrapper resourceUrl={pngSrcSet.src}>
            <source srcSet={avifSrcSet.srcSet} type="image/avif" />
            <source srcSet={webpSrcSet.srcSet} type="image/webp" />
            <Image {...pngSrcSet} />
          </DecryptionWrapper>
        </LazyImage>
      );
    } else {
      targetImageElement = (
        <LazyImage data-key="target" threshold={0.25}>
          {/* DecryptionWrapper is not required since it's not public */}
          <Image src={image} />
        </LazyImage>
      );
    }

    return (
      <StatefulImage>
        <StatefulImageWrapper data-key="loading">
          <Loader src={LoadingImage} />
        </StatefulImageWrapper>
        <StatefulImageWrapper data-key="error">
          <img src={GiftPlaceholder} />
        </StatefulImageWrapper>
        {targetImageElement}
      </StatefulImage>
    );
  }

  renderPanelGift() {
    const { i18nKey, id, price, streamId, t, type, hasEnoughBalance } =
      this.props;
    const isSelected = this.getIsSelected();
    const isInsufficient = !hasEnoughBalance;
    const isSendButtonLoading = this.getIsSendButtonLoading();
    return (
      <>
        <ImageWrapper onDragStart={e => e.preventDefault()}>
          {this.renderImage()}
        </ImageWrapper>
        {!isSelected && (
          <Title>
            {t(i18nKey, {
              ns: TranslationNamespace.GIFT,
            })}
          </Title>
        )}
        <Price $isSelected={isSelected} $isInsufficient={isInsufficient}>
          <Diamond
            type={type}
            src={
              isInsufficient && !isSelected ? DiamondGreyImage : DiamondImage
            }
          />
          {price}
        </Price>
        {isSelected && (
          <SendButton
            role="button"
            tabIndex={0}
            aria-disabled={isSendButtonLoading}
            $isLoading={isSendButtonLoading}
            data-element_id={
              hasEnoughBalance
                ? ButtonId.All.ButtonSend
                : ButtonId.All.ButtonTopUp
            }
            data-click_data__product_id={id}
            data-tracking_payload={{
              'product.id': id,
              'stream.id': streamId,
            }}
            onClick={this.handleSendButtonClick}
          >
            {isSendButtonLoading ? (
              <SendButtonSpinner aria-hidden="true" />
            ) : (
              t(
                hasEnoughBalance ? 'submit_gift' : 'top_up',
                hasEnoughBalance
                  ? { ns: TranslationNamespace.LIVESTREAM }
                  : { ns: TranslationNamespace.SETTINGS }
              )
            )}
          </SendButton>
        )}
      </>
    );
  }

  getIsSelected = () => {
    const { id } = this.props;
    const selectedGift = this.context?.selectedGift;
    return selectedGift?.id === id;
  };

  render() {
    const { streamId, type, id, isClickable } = this.props;
    const { isPagGiftPreviewPlaying } = this.state;
    const isSelected = this.getIsSelected();

    return (
      <GiftBorderWrapper $isSelected={isSelected}>
        <StyledGift
          data-element_id={giftButtonIdList[type]}
          data-click_data__product_id={id}
          data-tracking_payload={{
            'product.id': id,
            'stream.id': streamId,
          }}
          $isSelected={isSelected}
          $isPagGiftPreviewPlaying={isPagGiftPreviewPlaying}
          $isClickable={isClickable}
          onClick={isClickable ? this.handleOnClick : NULL_FUNCTION}
          onMouseEnter={isClickable ? this.handleOnMouseEnter : NULL_FUNCTION}
          onMouseLeave={isClickable ? this.handleOnMouseLeave : NULL_FUNCTION}
        >
          {this.renderPanelGift()}
        </StyledGift>
      </GiftBorderWrapper>
    );
  }
}

Gift.propTypes = {
  id: PropTypes.string,
  streamId: PropTypes.string,
  i18nKey: PropTypes.string,
  type: PropTypes.oneOf(giftAllowTypes),
  image: PropTypes.string,
  pagImage: PropTypes.string,
  price: PropTypes.number,
  isSwiping: PropTypes.bool,
  isAnimationGift: PropTypes.bool,
  isPagGift: PropTypes.bool,
  isActive: PropTypes.bool,
  isClickable: PropTypes.bool,
  isSendingGift: PropTypes.bool,
  giftSendingStatus: PropTypes.string,
  hasEnoughBalance: PropTypes.bool,
  isLoggedIn: PropTypes.bool,
  t: PropTypes.func.isRequired,
  sendGift: PropTypes.func,
  notifySendGift: PropTypes.func,
  pushToastr: PropTypes.func,
  addvalue: PropTypes.func,
  login: PropTypes.func,
  openAnimationFailedModal: PropTypes.func,
};

Gift.defaultProps = {
  id: '',
  streamId: '',
  type: giftAllowTypes[0],
  i18nKey: '',
  image: '',
  pagImage: '',
  price: 0,
  isAnimationGift: false,
  isPagGift: false,
  isActive: false,
  isClickable: true,
  isSendingGift: false,
  giftSendingStatus: null,
  hasEnoughBalance: false,
  isLoggedIn: false,
  sendGift: NULL_FUNCTION,
  notifySendGift: NULL_FUNCTION,
  pushToastr: NULL_FUNCTION,
  addvalue: NULL_FUNCTION,
  login: NULL_FUNCTION,
  openAnimationFailedModal: NULL_FUNCTION,
};

const GiftBorderWrapper = styled.div`
  max-width: 108px;
  width: 100%;
  height: 126px;
  box-sizing: border-box;
  padding: 1px;
  border-radius: 8px;
  background: ${({ $isSelected }) =>
    $isSelected ? color.giftSelectionGradient : 'transparent'};
`;

export const ImageWrapper = styled.div`
  width: 72px;
  height: 72px;
  transition: transform 0.2s ease;
`;

export const StyledGift = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  font-size: 12px;
  border-radius: 8px;
  background: ${({ $isSelected }) =>
    $isSelected
      ? `${color.giftSelectionOverlay} ${color.neutral[0]}`
      : 'transparent'};
  gap: 2px;
  box-sizing: border-box;
  cursor: ${({ $isClickable }) =>
    $isClickable === false ? 'default' : 'pointer'};
  ${({ $isPagGiftPreviewPlaying }) =>
    $isPagGiftPreviewPlaying
      ? ''
      : `
    @media (hover: hover) and (pointer: fine) {
      &:hover ${ImageWrapper} {
        transform: scale(1.2);
      }
    }
  `}
`;

const StatefulImageWrapper = styled.div`
  width: 100%;
  height: 100%;
  text-align: center;

  & > img {
    height: 100%;
    width: 100%;
  }
`;

const Loader = styled.img``;

const Title = styled.div`
  margin: 0;
  font-size: 10px;
  width: 100%;
  color: #828282;
  text-align: center;
  line-height: 12px;
  ${media.tablet`
    font-size: 12px;
  `}
`;

const Price = styled.div`
  display: flex;
  align-items: center;
  height: 14px;
  color: ${({ $isSelected, $isInsufficient }) => {
    if (!$isInsufficient) {
      return textColor.primary;
    }
    return $isSelected ? textColor.danger : color.grey['500'];
  }};
`;

const SendButton = styled.div`
  align-self: stretch;
  box-sizing: border-box;
  padding: 2px 12px;
  border-radius: 0px 0px 6px 6px;
  background: ${color.giftSelectionGradient};
  color: ${textColor.white};
  font-size: 12px;
  line-height: 16px;
  user-select: none;
  text-align: center;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: ${({ $isLoading }) => ($isLoading ? 0.6 : 1)};
  cursor: ${({ $isLoading }) => ($isLoading ? 'progress' : 'pointer')};
  pointer-events: ${({ $isLoading }) => ($isLoading ? 'none' : 'auto')};
`;

const SendButtonSpinner = styled.span`
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid ${color.neutral['100_alpha_35']};
  border-top-color: ${textColor.white};
  border-radius: 50%;
  animation: gift-send-button-spin 0.8s linear infinite;
  @keyframes gift-send-button-spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const Diamond = styled.img`
  vertical-align: top;
  height: ${({ type }) => (type === giftAllowTypes[0] ? '15px' : '12px')};
  margin-right: 0.3em;
`;

const ApngGiftContainer = styled.div`
  width: 72px;
  height: 72px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CanvasLayer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;

  transition: opacity 0.4s ease-in;

  opacity: ${({ $isLoading }) => {
    if ($isLoading) return 0.3;
    return 1;
  }};

  pointer-events: ${({ $isLoading }) => {
    return $isLoading ? 'none' : 'auto';
  }};
`;

const AnimationGift = styled.canvas`
  width: 72px;
  height: 72px;
`;

const PagGiftContainer = styled.div`
  width: 72px;
  height: 72px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const PngImageLayer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;

  transition: opacity 0.4s ease-in;

  opacity: ${({ $isLoading }) => {
    if ($isLoading) return 0.3;
    return 1;
  }};

  pointer-events: ${({ $isLoading }) => {
    return $isLoading ? 'none' : 'auto';
  }};

  ${({ $isHovered }) =>
    $isHovered
      ? `
    animation: breathe 1.0s ease-in-out infinite;
  `
      : ''}

  @keyframes breathe {
    0%,
    100% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.1);
    }
  }

  /* Use visibility (not display / unmount) so the underlying <img> never
     reloads when the PAG overlay tears down and we bring the thumbnail back. */
  visibility: ${({ $isHidden }) => ($isHidden ? 'hidden' : 'visible')};
`;

const PAG_PREVIEW_SCALE = 1.5;
const PagPreviewLayer = styled.div`
  position: absolute;
  top: -${(PAG_PREVIEW_SCALE - 1) * 50}%;
  left: -${(PAG_PREVIEW_SCALE - 1) * 50}%;
  width: ${PAG_PREVIEW_SCALE * 100}%;
  height: ${PAG_PREVIEW_SCALE * 100}%;
  z-index: 2;
  pointer-events: none;
`;

const CenterIconOverlay = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
`;

const TopRightIconOverlay = styled.div`
  position: absolute;
  top: 0px;
  right: 0px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SpinningLoading = styled.img`
  width: 100%;
  height: 100%;
`;

const DownloadIconImage = styled.img`
  width: 100%;
  height: 100%;
`;

const WarningIconImage = styled.img`
  width: 100%;
  height: 100%;
`;

export default withTranslation()(Gift);
